/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import type { ReactNode } from 'react'

import disableScroll from 'disable-scroll'
import { useCallback, useState } from 'react'
import { createPortal } from 'react-dom'

export type CustomStyled = {
  wrapper?: React.CSSProperties
  overlay?: React.CSSProperties
  content?: React.CSSProperties
}

export interface ModalProps {
  children?: React.ReactNode
  isOpen: boolean
  onOverlayClick: React.MouseEventHandler<HTMLDivElement>
  customStyled?: CustomStyled
  elementId: 'root' | string
}

export interface ModalOptions {
  preventScroll?: boolean
  closeOnOverlayClick?: boolean
  customStyled?: CustomStyled
}

export type UseModal = (
  elementId: string,
  options?: ModalOptions,
) => [
  ModalWrapper: React.FC<{ children?: React.ReactNode }>,
  open: () => void,
  close: () => void,
  isOpen: boolean,
]

const wrapperStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 100000,
}

const containerStyle: React.CSSProperties = {
  position: 'relative',
  zIndex: 100001,
}

const Modal: React.FC<ModalProps> = ({
  children,
  isOpen = false,
  onOverlayClick,
  customStyled = {},
  elementId = 'root',
}) => {
  if (isOpen === false) {
    return null
  }

  const { wrapper = {}, overlay = {}, content = {} } = customStyled

  return createPortal(
    <div style={{ ...wrapperStyle, ...wrapper }}>
      <div style={{ ...overlayStyle, ...overlay }} onClick={onOverlayClick} />
      <div style={{ ...containerStyle, ...content }}>{children}</div>
    </div>,
    document.getElementById(elementId) as HTMLElement,
  ) as ReactNode
}

export const useModal: UseModal = (elementId = 'root', options = {}) => {
  const { preventScroll = false, closeOnOverlayClick = true, customStyled = {} } = options
  const [isOpen, setOpen] = useState<boolean>(false)
  const open = useCallback(() => {
    setOpen(true)
    if (preventScroll) {
      disableScroll.on()
    }
  }, [setOpen, preventScroll])
  const close = useCallback(() => {
    setOpen(false)
    if (preventScroll) {
      disableScroll.off()
    }
  }, [setOpen, preventScroll])
  const onOverlayClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation()
      if (closeOnOverlayClick) {
        close()
      }
    },
    [closeOnOverlayClick, close],
  )

  const ModalWrapper = useCallback(
    ({ children }: { children?: React.ReactNode }) => (
      <Modal
        isOpen={isOpen}
        onOverlayClick={onOverlayClick}
        elementId={elementId}
        customStyled={customStyled}
      >
        {children}
      </Modal>
    ),
    [isOpen, onOverlayClick, customStyled, elementId],
  )

  return [ModalWrapper, open, close, isOpen]
}
